import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  CheckCircle2,
  Eye,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  X,
} from "lucide-react";

import BankAccountFields from "features/courier-onboarding/components/fields/BankAccountFields";
import OnboardingPanelFooter from "features/courier-onboarding/components/OnboardingPanelFooter";
import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import { submitCourierOnboarding } from "features/courier-onboarding/services/courierOnboardingService";
import {
  isRequiredDocumentReady,
  requiredDocsByTrack,
  type CourierDocument,
  type StepKey,
} from "features/courier-onboarding/types";
import {
  validatePersonalInfo,
  validateBankAccountInfo,
} from "features/courier-onboarding/schemas";
import { normalizeLang } from "features/courier-onboarding/components/welcome/welcomeContent";
import {
  BANK_FIELD_ORDER,
  firstInvalidFieldKey,
  scrollToInvalidField,
} from "features/courier-onboarding/utils/scrollToInvalidField";

const isImageDoc = (doc: CourierDocument): boolean =>
  Boolean(doc.type?.startsWith("image/")) ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(doc.name);

const isPdfDoc = (doc: CourierDocument): boolean =>
  doc.type === "application/pdf" || /\.pdf$/i.test(doc.name);

interface Props {
  onSubmitted: (submissionId: string) => void;
  onBack: () => void;
  onEdit: (step: StepKey) => void;
}

interface FieldItem {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

const Field: React.FC<FieldItem> = ({ label, value, mono }) => {
  const isEmpty =
    value === null || value === undefined || value === "" || value === "-";
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <span
        className={`text-sm ${isEmpty ? "text-gray-300" : "text-gray-900"} ${
          mono ? "font-mono tracking-tight" : "font-medium"
        } truncate`}
        title={typeof value === "string" ? value : undefined}
      >
        {isEmpty ? "—" : value}
      </span>
    </div>
  );
};

interface SectionProps {
  title: string;
  /** Omitted for sections that are edited in place rather than on another step. */
  onEdit?: () => void;
  editLabel: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  title,
  onEdit,
  editLabel,
  children,
}) => (
  <section
    className="border-s-2 border-[#bf0035] bg-[#bf0035]/[0.035] px-4 py-5"
  >
    <div className="flex items-center justify-between gap-3">
      <h3 className="truncate text-base font-semibold text-gray-900">
        {title}
      </h3>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-[#bf0035]/20 hover:bg-[#bf0035]/[0.05] hover:text-[#bf0035]"
        >
          <Pencil size={13} strokeWidth={2.4} />
          <span>{editLabel}</span>
        </button>
      ) : null}
    </div>
    <div className="pt-4">{children}</div>
  </section>
);

const ReviewStep: React.FC<Props> = ({ onSubmitted, onBack, onEdit }) => {
  const { t, i18n } = useTranslation();
  const { data, setMetadata } = useCourierOnboarding();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidSection, setInvalidSection] = useState<StepKey | null>(null);
  const [showStatusLink, setShowStatusLink] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<CourierDocument | null>(null);
  // Bank details are edited in place here, so their validation errors surface
  // on the fields themselves instead of routing to a separate step.
  const [bankErrors, setBankErrors] = useState<Record<string, string>>({});
  // Courier must explicitly affirm that the data they entered is accurate
  // before we accept a submission. Resets per mount so a back-and-forth
  // edit always re-asks for confirmation.
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (Object.keys(bankErrors).length === 0) return;
    const first = firstInvalidFieldKey(bankErrors, BANK_FIELD_ORDER);
    if (first) scrollToInvalidField(first);
  }, [bankErrors]);

  const docs = useMemo(() => {
    const trackKeys = data.metadata.businessTrack
      ? requiredDocsByTrack[data.metadata.businessTrack]
      : null;
    return trackKeys
      ? data.documents.filter((d) => trackKeys.includes(d.key))
      : data.documents;
  }, [data.documents, data.metadata.businessTrack]);

  const submit = async () => {
    if (!confirmed) return;

    const track = data.metadata.businessTrack;
    if (!track) {
      setInvalidSection("documents");
      setError("onboarding.errors.businessTrackRequired");
      return;
    }

    // Full pre-submit re-validation. A user can reach Review with an invalid
    // state by editing an earlier section (e.g. replacing a document with a
    // wrong file that cleared OCR-prefilled fields), so we re-check every
    // section here and route the user back to the offending step.
    if (Object.keys(validatePersonalInfo(data.personalInfo)).length > 0) {
      setInvalidSection("personal");
      setError("onboarding.errors.reviewPersonalIncomplete");
      return;
    }
    if (data.vehicleInfo.vehicleTypes.length === 0) {
      setInvalidSection("personal");
      setError("onboarding.errors.vehicleRequired");
      return;
    }
    if (track === "LICENSED_BUSINESS") {
      const bankErrs = validateBankAccountInfo(data.bankAccountInfo);
      setBankErrors(bankErrs);
      if (Object.keys(bankErrs).length > 0) {
        setInvalidSection(null);
        setError("onboarding.errors.reviewBankIncomplete");
        return;
      }
    }
    const requiredKeys = requiredDocsByTrack[track];
    const documentsComplete = requiredKeys.every((k) =>
      isRequiredDocumentReady(k, data.documents),
    );
    if (!documentsComplete) {
      setInvalidSection("documents");
      setError("onboarding.errors.reviewDocumentsIncomplete");
      return;
    }

    setInvalidSection(null);
    setSubmitting(true);
    setError(null);
    setShowStatusLink(false);
    try {
      const result = await submitCourierOnboarding({
        businessTrack: track,
        personalInfo: data.personalInfo,
        vehicleInfo: { vehicleTypes: data.vehicleInfo.vehicleTypes },
        experienceInfo: data.experienceInfo,
        documents: data.documents.map(({ key, name, type, size, url }) => ({
          key,
          name,
          type,
          size,
          url,
        })),
        bankAccountInfo:
          track === "LICENSED_BUSINESS" ? data.bankAccountInfo : undefined,
        notes: data.notes || undefined,
        phoneVerificationToken: data.metadata.phoneVerificationToken ?? "",
        metadata: {
          ...data.metadata,
          language: normalizeLang(i18n.language),
          phoneVerificationToken: undefined,
        },
      });
      setMetadata({ submissionId: result.submissionId });
      onSubmitted(result.submissionId);
    } catch (err) {
      const code = (
        err as {
          response?: { data?: { code?: string } };
        }
      )?.response?.data?.code;
      switch (code) {
        case "ALREADY_REGISTERED":
          setError("onboarding.errors.alreadyRegistered");
          setShowStatusLink(true);
          break;
        case "UNDERAGE":
          setError("onboarding.errors.underage");
          break;
        case "DOCS_MISMATCH":
          setError("onboarding.errors.documentsIdMismatch");
          break;
        case "PHONE_NOT_VERIFIED":
          setError("onboarding.errors.phoneNotVerified");
          break;
        case "PHONE_VERIFICATION_EXPIRED":
          setError("onboarding.errors.phoneVerificationExpired");
          break;
        default:
          setError("onboarding.errors.submitFailed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const editLabel = t("onboarding.review.editSection") as string;

  return (
    <div className="flex flex-col gap-5">
      <Section
        title={t("onboarding.review.documents")}
        onEdit={() => onEdit("documents")}
        editLabel={editLabel}
      >
        {data.metadata.businessTrack ? (
          <div className="mb-4">
            <Field
              label={t("onboarding.review.businessTrack")}
              value={
                t(
                  `onboarding.docOrder.tracks.${
                    data.metadata.businessTrack === "FINANCIAL_COMPANY"
                      ? "financialCompany"
                      : "licensedBusiness"
                  }.title`,
                ) as string
              }
            />
          </div>
        ) : null}

        {docs.length === 0 ? (
          <p className="text-sm text-gray-400">
            {t("onboarding.review.noFiles")}
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {docs.map((d) => {
              const canPreview = Boolean(d.url);
              const isImage = isImageDoc(d);
              return (
                <li key={d.key}>
                  <button
                    type="button"
                    onClick={() => canPreview && setPreviewDoc(d)}
                    disabled={!canPreview}
                    className={`group flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-start transition-colors ${
                      canPreview
                        ? "cursor-pointer hover:border-[#bf0035]/30 hover:bg-[#bf0035]/[0.02]"
                        : "cursor-default opacity-80"
                    }`}
                  >
                    <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#bf0035]/[0.05] text-[#bf0035]">
                      {isImage && d.url ? (
                        <img
                          src={d.url}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <FileText size={16} strokeWidth={2.2} />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        {t(`onboarding.documents.${d.key}`)}
                      </span>
                      <span
                        className="truncate text-sm font-medium text-gray-800"
                        title={d.name}
                      >
                        {d.name}
                      </span>
                    </div>
                    {canPreview ? (
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors group-hover:text-[#bf0035]">
                        <Eye size={14} strokeWidth={2.4} />
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {data.metadata.businessTrack === "LICENSED_BUSINESS" ? (
        <Section
          title={t("onboarding.bankAccount.title")}
          editLabel={editLabel}
        >
          <BankAccountFields errors={bankErrors} />
        </Section>
      ) : null}

      <Section
        title={t("onboarding.review.personal")}
        onEdit={() => onEdit("personal")}
        editLabel={editLabel}
      >
        <div className="flex flex-col gap-4">
          <Field
            label={t("onboarding.personal.fullName")}
            value={data.personalInfo.fullName}
          />
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Field
              label={t("onboarding.personal.taxId")}
              value={data.personalInfo.taxId}
              mono
            />
            <Field
              label={t("onboarding.personal.dateOfBirth")}
              value={data.personalInfo.dateOfBirth}
              mono
            />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Field
              label={t("onboarding.personal.phoneNumber")}
              value={data.personalInfo.phoneNumber}
              mono
            />
            <Field
              label={t("onboarding.personal.email")}
              value={data.personalInfo.email}
            />
          </div>
        </div>
      </Section>

      <Section
        title={t("onboarding.personal.detailsTitle")}
        onEdit={() => onEdit("personal")}
        editLabel={editLabel}
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field
            label={t("onboarding.personal.city")}
            value={data.personalInfo.city}
          />
          <Field
            label={t("onboarding.personal.spokenLanguages")}
            value={
              data.personalInfo.spokenLanguages.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {data.personalInfo.spokenLanguages.map((lang) => (
                    <span
                      key={lang}
                      className="rounded-full bg-[#bf0035]/[0.06] px-2 py-0.5 text-xs font-semibold text-[#bf0035]"
                    >
                      {lang}
                    </span>
                  ))}
                </span>
              ) : (
                ""
              )
            }
          />
          <Field
            label={t("onboarding.vehicle.title")}
            value={
              data.vehicleInfo.vehicleTypes.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {data.vehicleInfo.vehicleTypes.map((v) => (
                    <span
                      key={v}
                      className="rounded-full bg-[#bf0035]/[0.06] px-2 py-0.5 text-xs font-semibold text-[#bf0035]"
                    >
                      {t(`onboarding.vehicle.${v}`) as string}
                    </span>
                  ))}
                </span>
              ) : (
                ""
              )
            }
          />
          <Field
            label={t("onboarding.experience.priorExperience")}
            value={
              data.experienceInfo.hasDeliveryExperience === null
                ? ""
                : data.experienceInfo.hasDeliveryExperience
                  ? (t("common.yes") as string)
                  : (t("common.no") as string)
            }
          />
          {data.experienceInfo.hasDeliveryExperience &&
          data.experienceInfo.yearsOfExperienceRange ? (
            <Field
              label={t("onboarding.experience.yearsLabel")}
              value={
                t(
                  `onboarding.experience.${data.experienceInfo.yearsOfExperienceRange}`,
                ) as string
              }
            />
          ) : null}
        </div>
      </Section>

      {error ? (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-haat-red-200 bg-haat-red-50 px-4 py-3 text-sm font-medium text-haat-red-700"
        >
          <p>{t(error, { minAge: 16 })}</p>
          {invalidSection ? (
            <button
              type="button"
              onClick={() => {
                const target = invalidSection;
                setInvalidSection(null);
                setError(null);
                onEdit(target);
              }}
              className="inline-flex mt-1.5 text-xs font-semibold text-haat-red-700 underline underline-offset-2"
            >
              {t("onboarding.review.fixSection")}
            </button>
          ) : null}
          {showStatusLink ? (
            <Link
              to="/login"
              className="inline-flex mt-1.5 text-xs font-semibold text-haat-red-700 underline underline-offset-2"
            >
              {t("onboarding.errors.alreadyRegisteredCta")}
            </Link>
          ) : null}
        </motion.div>
      ) : null}

      <button
        type="button"
        role="checkbox"
        aria-checked={confirmed}
        onClick={() => setConfirmed(!confirmed)}
        disabled={submitting}
        className="flex w-full cursor-pointer items-start gap-3 border-t border-gray-200 py-5 text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          aria-hidden="true"
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ring-1 transition-colors ${
            confirmed
              ? "bg-[#bf0035] text-white ring-[#bf0035]"
              : "bg-white text-transparent ring-gray-300"
          }`}
        >
          <Check size={14} strokeWidth={3} />
        </span>
        <span
          className={`text-sm leading-relaxed ${
            confirmed ? "text-[#bf0035]" : "text-gray-800"
          }`}
        >
          {t("onboarding.review.confirmationLabel")}
        </span>
      </button>

      <OnboardingPanelFooter>
        <div className="flex flex-col gap-2">
          {error ? (
            <p className="text-xs font-medium text-haat-red-700">
              {t(error, { minAge: 16 })}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="btn-outline"
            disabled={submitting}
          >
            {t("common.back")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !confirmed}
            className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <CheckCircle2 size={18} strokeWidth={2.4} />
            )}
            <span>{t("common.submit")}</span>
          </button>
          </div>
        </div>
      </OnboardingPanelFooter>

      <AnimatePresence>
        {previewDoc ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setPreviewDoc(null)}
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                <div className="flex min-w-0 flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    {t(`onboarding.documents.${previewDoc.key}`)}
                  </span>
                  <span
                    className="truncate text-sm font-semibold text-gray-900"
                    title={previewDoc.name}
                  >
                    {previewDoc.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {previewDoc.url ? (
                    <a
                      href={previewDoc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-gray-200 px-3 text-xs font-semibold text-gray-700 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
                    >
                      <ExternalLink size={14} strokeWidth={2.4} />
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setPreviewDoc(null)}
                    aria-label={t("common.close") as string}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
                  >
                    <X size={18} strokeWidth={2.4} />
                  </button>
                </div>
              </div>
              <div className="flex flex-1 items-center justify-center overflow-auto bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                {previewDoc.url ? (
                  isImageDoc(previewDoc) ? (
                    <img
                      src={previewDoc.url}
                      alt={previewDoc.name}
                      className="max-h-[78vh] max-w-full rounded-xl object-contain shadow-md"
                    />
                  ) : isPdfDoc(previewDoc) ? (
                    <iframe
                      src={previewDoc.url}
                      title={previewDoc.name}
                      className="h-[78vh] w-full rounded-xl border-0 bg-white shadow-md"
                    />
                  ) : (
                    <a
                      href={previewDoc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-purple-700"
                    >
                      <ExternalLink size={16} strokeWidth={2.4} />
                      {t("common.openInNewTab")}
                    </a>
                  )
                ) : (
                  <p className="text-sm text-gray-500">
                    {t("onboarding.review.noFiles")}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ReviewStep;
